// The fight: owns the sim, the clock and input for one match (a sequence of
// rounds). Inputs carry their event timestamp and are applied at that exact
// point in sim time, so timing judgements don't depend on the frame rate.
// Menus live in ui/app.ts; the game only reports how a match ended.

import { mulberry32 } from '@/core/rng';
import { Sim, chainTier, inReach, type RoundSpec, type Side, type SimEvent } from '@/sim/sim';
import { BOSS, KINDS, TUNING as T, type BossId } from '@/sim/tuning';
import {
  AIRBORNE,
  Renderer,
  contactTime,
  loadImpactKit,
  padContact,
  padOffset,
  type Maai,
  type PadStep,
} from '@/render/renderer';
import { Sfx } from '@/audio/sfx';
import { bpmFor, roundsFor, seedFor, simOptionsFor, stageFor, type Mode } from '@/meta/modes';
import { EXAMS } from '@/content/exams';
import { fmt, t } from '@/i18n';
import { track } from '@/meta/analytics';
import type { Dojo } from '@/content/dojos';
import { loadRoom, type StageId } from '@/render/stages';
import { loadBossLooks } from '@/render/rig';
import type { Training } from '@/meta/streak';
import { Coach, NO_COACH, type CoachPlan } from '@/app/coach';

type Input = { t: number; action: Side | 'kiai' };

/** banner: "Round 2 — Hajime!" with the sim frozen; fight; cleared: corpses fly, no spawns. */
type Phase = 'idle' | 'banner' | 'fight' | 'cleared' | 'ending' | 'impact';

export interface MatchResult {
  mode: Mode;
  /** The opponents' seed: a shared link replays them. */
  seed: number;
  passed: boolean;
  /** Rounds fully cleared. */
  rounds: number;
  totalRounds: number;
  score: number;
  kills: number;
  hits: number;
  perfects: number;
  bestCombo: number;
  /** Longest run of PERFECT/COUNTER hits. */
  bestChain: number;
  /** Hits the player took (none: a flawless run). */
  hitsTaken: number;
  seconds: number;
  /** Levels climbed in the match (Kumite ramps), 0 at its start. */
  level: number;
  /** Impact test reading(s) at the end of the exam (0–999). */
  impacts?: number[];
  /** Filled in by the menus once the result is recorded. */
  promoted?: boolean;
  newBest?: boolean;
  /** A daily exam's training day, once counted. */
  training?: Training;
  /** A friend's challenge that doesn't count toward progress (a locked exam, a past day). */
  practice?: boolean;
  /** Mastery marks this run added (MARK bits). */
  newMarks?: number;
}

const MAX_FRAME = 0.1;
const HIT_STOP = 0.05;
/** CSS px a touch moves before it's a swipe (a tap's thumb wobbles less). */
const SWIPE_PX = 30;
/** The impact test's gyaku-zuki reaches the pad this long after the tap. */
const PAD_LANDS = contactTime('gyakuZuki');
const FINAL_BLOW_SLOWMO = 0.55;
/** Average frame time (s) above which the quality governor steps in (~40 fps). */
const SLOW_FRAME = 1 / 40;
const BANNER_TIME = 1.3;
const CLEARED_TIME = 0.9;
const ENDING_TIME = 0.8;
/** Impact test: its banner's life, how long the meter waits while the call
 *  is read, how long the reading shows, and when it strikes by itself once
 *  the meter swings (later where the senpai steps: the right distance has to
 *  come round). */
const IMPACT_BANNER = 1.4;
const IMPACT_INTRO = 1.1;
const IMPACT_AFTER = 1.5;
const IMPACT_AUTO = 6;
export const IMPACT_AUTO_MAAI = 8;
export const IMPACT_MAX = 999;

/** Restart a HUD element's little pop animation. */
function bump(node: HTMLElement): void {
  node.classList.remove('bump');
  void node.offsetWidth;
  node.classList.add('bump');
}

/**
 * Impact reading for a meter position: the top 10% is full power (999),
 * below that it falls off steeply, so timing is what makes the impact.
 */
export function impactFor(meter: number): number {
  if (meter >= 0.9) return IMPACT_MAX;
  const k = Math.max(0, Math.min(1, (meter - 0.2) / 0.7));
  return Math.min(950, Math.round(IMPACT_MAX * k ** 1.4));
}

/** Per exam, how much the meter's speed may swing (±share of its pace). */
const METER_FLUTTER = 0.07;
const METER_FLUTTER_MAX = 0.5;
/** The waves its speed changes on (seconds, weight): two slower than a swing,
 *  so each swing has a pace of its own, and one that shifts it mid-climb. */
const METER_WAVES = [
  [3.3, 0.45],
  [5.9, 0.3],
  [0.9, 0.25],
] as const;

/**
 * Seconds a fluttering meter has run ahead of (or behind) a steady one, `t`
 * seconds in: its speed is 1 + a·Σ wᵢ·sin(ωᵢt + φᵢ), integrated. The seed picks
 * the phases, so a shared link replays the same meter; a ≤ 0.5 keeps it from
 * ever stopping or turning back.
 */
function meterDrift(t: number, exam: number, seed: number): number {
  const a = Math.min(METER_FLUTTER_MAX, exam * METER_FLUTTER);
  if (a === 0) return 0;
  const rng = mulberry32(seed ^ 0x6d657465);
  let drift = 0;
  for (const [period, weight] of METER_WAVES) {
    const w = (2 * Math.PI) / period;
    const phase = rng.next() * 2 * Math.PI;
    drift += (weight * (Math.cos(phase) - Math.cos(w * t + phase))) / w;
  }
  return a * drift;
}

/**
 * Meter position `t` seconds into the impact test of exam `exam` (0-based) in
 * the match on `seed`. It climbs and falls in a beat but eases in on the way
 * up, so it crawls through the low end and flashes through the full-power
 * top; higher belts swing faster, climb steeper and keep changing speed (none
 * in the first exam, up to ±49 % in the black belt exam), so the top can't be
 * hit by counting the beat.
 */
export function meterAt(t: number, exam: number, seed = 0): number {
  const period = 1.9 - exam * 0.06;
  const phase = ((t + meterDrift(t, exam, seed)) / period) % 1;
  const p = 1 - Math.abs(1 - phase * 2);
  return p ** (1.6 + exam * 0.1);
}

/** From this exam on (the green belt's, and the daily exam's), the senpai
 *  holding the pad steps in and out: the punch only has its full power at the
 *  right distance (maai). */
export const MAAI_FROM = 3;
/** How far the pad may be from where the fist fully reaches and still take a
 *  full punch (world units): a hair further, or a little nearer (it lands a
 *  touch early). */
const MAAI_FAR_TOL = 0.02;
const MAAI_NEAR_TOL = 0.05;
/** Nearer still, the punch is jammed: its share of the reading falls off this
 *  fast (per world unit), but it still pushes this much. */
const MAAI_FALL = 3;
const MAAI_JAMMED = 0.4;

/** How a punch meets the pad `d` further off than the fist fully reaches. */
export function maaiOf(d: number): Maai {
  return d > MAAI_FAR_TOL ? 'far' : d < -MAAI_NEAR_TOL ? 'close' : 'in';
}

/** The share of the meter's reading a punch gets with the pad `d` off: none if it falls short. */
export function maaiFactor(d: number): number {
  const maai = maaiOf(d);
  if (maai !== 'close') return maai === 'in' ? 1 : 0;
  return Math.max(MAAI_JAMMED, 1 - (-d - MAAI_NEAR_TOL) * MAAI_FALL);
}

/** The senpai's stops with the pad: from `at` seconds into the meter's swing
 *  he steps, over `step` seconds, to `d` world units off the right distance. */
interface MaaiPlan {
  step: number;
  stops: { at: number; d: number }[];
}

let planned: { exam: number; seed: number; plan: MaaiPlan } | null = null;

/**
 * The senpai's steps in the impact test of exam `exam` (≥ MAAI_FROM) on `seed`:
 * he starts a step too close or too far, steps in to the right distance after
 * the meter's first swing or so and holds it a while, then a step off (either
 * way) and back, and so on. Higher belts step further and quicker and hold the
 * right distance less long; the seed picks the rest, so a shared link replays
 * the same steps.
 */
function maaiPlan(exam: number, seed: number): MaaiPlan {
  if (planned?.exam === exam && planned.seed === seed) return planned.plan;
  const k = Math.min(1, (exam - MAAI_FROM) / (EXAMS.length - 1 - MAAI_FROM));
  // (any nearer and the pad would be in the karateka's guard)
  const near = -(0.15 + 0.03 * k);
  const far = 0.14 + 0.08 * k;
  const step = 0.4 - 0.1 * k;
  const hold = 2.4 - 0.4 * k;
  const rng = mulberry32(seed ^ 0x6d616169);
  const off = (): number => (rng.next() < 0.5 ? near : far);
  const stops = [{ at: -Infinity, d: off() }];
  for (let t = 0.9 + rng.next() * 0.4; t < 40;) {
    stops.push({ at: t, d: 0 });
    t += step + hold * (0.85 + rng.next() * 0.3);
    stops.push({ at: t, d: off() });
    t += step + 0.9 + rng.next() * 0.6;
  }
  const plan = { step, stops };
  planned = { exam, seed, plan };
  return plan;
}

const STILL: PadStep = { from: 0, to: 0, u: 1 };

/** The senpai's step `t` seconds into the meter's swing in the impact test of exam `exam` on `seed`. */
export function maaiAt(t: number, exam: number, seed = 0): PadStep {
  if (exam < MAAI_FROM) return STILL;
  const { step, stops } = maaiPlan(exam, seed);
  let i = 0;
  while (i + 1 < stops.length && stops[i + 1]!.at <= t) i++;
  const stop = stops[i]!;
  return { from: (stops[i - 1] ?? stop).d, to: stop.d, u: Math.min(1, (t - stop.at) / step) };
}

/**
 * Where the pad is off the right distance as a punch tapped `swing` seconds
 * into the meter's swing meets it, and how long after the tap: the nearer the
 * pad, the sooner the fist gets there, and the senpai stops there.
 */
export function padMeet(swing: number, exam: number, seed: number): { d: number; lands: number } {
  let lands = PAD_LANDS;
  let d = padOffset(maaiAt(swing + lands, exam, seed));
  for (let i = 0; i < 4; i++) {
    lands = padContact(d);
    d = padOffset(maaiAt(swing + lands, exam, seed));
  }
  return { d, lands };
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

export class Game {
  sim: Sim = new Sim(mulberry32(1));
  phase: Phase = 'idle';
  mode: Mode = { kind: 'kumite' };
  /** The match's rounds, fixed when it starts. */
  private rounds: readonly RoundSpec[] = [];
  private seed = 1;
  /** The stage boss on the mat, if he's in. */
  private boss: BossId | null = null;
  private clearedFor = CLEARED_TIME;
  private hitsTaken = 0;
  round = 0;
  readonly bot: boolean;
  onEnd: (result: MatchResult) => void = () => undefined;
  onPauseChange: (paused: boolean) => void = () => undefined;
  /** The player showed they know something the coach taught (COACHED bits). */
  onCoached: (bits: number) => void = () => undefined;
  paused = false;

  private readonly renderer: Renderer;
  readonly sfx = new Sfx();
  private inputs: Input[] = [];
  private lastReal = 0;
  private acc = 0;
  private hitStop = 0;
  private slowmo = 0;
  private coach = new Coach();
  /** Touches down in the fight (by pointer id): where they came down, and
   *  whether they've moved like a swipe since. */
  private touches = new Map<number, { x: number; y: number; swiped: boolean }>();
  /** The last strike came from a key, not a tap: the coach names the key. */
  private keyboard = !window.matchMedia('(pointer: coarse)').matches;
  /** The last PERFECT-chain hit was a COUNTER: the HUD names the chain so. */
  private countered = false;
  /** Impact sounds waiting for their strike to reach the opponent (sim time). */
  private landings: { at: number; play: () => void }[] = [];
  /** Sim time the HUD's counts wait for: the strikes on their way landing. */
  private counted = 0;
  private phaseT = 0;
  private passed = false;
  private frameAvg = 1 / 60;
  private slowFor = 0;
  /** Impact test between rounds: meter position, reading once struck (and
   *  seconds since), how far into the meter's swing, when the punch lands and
   *  how it met the pad. */
  private impact: {
    meter: number;
    t: number;
    value: number | null;
    swing: number;
    lands: number;
    maai: Maai | null;
  } | null = null;
  private impacts: number[] = [];
  private hud = {
    hearts: el('hud-hearts'),
    score: el('hud-score'),
    chain: el('hud-chain'),
    round: el('hud-round'),
    kiaiFill: el('kiai-fill'),
    kiaiBtn: el<HTMLButtonElement>('kiai-btn'),
    kiaiTip: el('kiai-tip'),
    tapTip: el('tap-tip'),
    target: el('hud-target'),
  };
  private shown = {
    hearts: -1,
    score: -1,
    chain: '',
    kiai: -1,
    tier: -1,
    round: '',
    target: '',
    tip: '',
    tapTip: false,
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.bot = new URLSearchParams(location.search).get('bot') === '1';
    this.renderer = new Renderer(canvas);
    this.bindInput();
    // ResizeObserver also catches mobile browser bars collapsing, which
    // doesn't always fire window 'resize' and would stretch the canvas
    new ResizeObserver(() => this.renderer.resize()).observe(canvas);
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.renderer.reducedMotion = motion.matches;
    motion.addEventListener('change', () => (this.renderer.reducedMotion = motion.matches));
    document.addEventListener('visibilitychange', () => {
      // Drop the gap so a backgrounded tab doesn't fast-forward the fight,
      // and never let opponents keep swinging while the player looks away
      this.lastReal = performance.now();
      if (document.hidden && this.playing) this.setPaused(true);
    });
    requestAnimationFrame((t) => this.frame(t));
  }

  get playing(): boolean {
    return this.phase !== 'idle';
  }

  /** The player's chosen dojo: its room for the dojo stage, its name on the sign. */
  setDojo(dojo: Dojo | null): void {
    this.renderer.sign = dojo?.name ?? null;
    this.renderer.dojoStyle = dojo?.style ?? 'kamiza';
    void loadRoom(this.renderer.dojoStyle);
  }

  /** Between matches: the stage behind the menus (the dojo picker shows the room). */
  showStage(stage: StageId): void {
    if (this.playing) return;
    this.renderer.stage = stage;
    document.body.dataset.stage = stage;
  }

  setBelt(color: string, edge: string): void {
    this.renderer.beltColor = color;
    this.renderer.beltEdge = edge;
  }

  /** Start a match; `hint` shows where to tap (the first exam, a newcomer's
   *  first fight), `coach` what the coach shows in it (app/coach.ts). */
  play(
    mode: Mode,
    hint = mode.kind === 'exam' && mode.exam === 0,
    coach: CoachPlan = NO_COACH,
  ): void {
    this.sfx.unlock();
    this.mode = mode;
    this.rounds = roundsFor(mode);
    const first = this.rounds[0];
    if (!first) throw new Error('Mode has no rounds');
    // A stage boss waits at the end: his outfit loads while the rounds are fought
    if (this.rounds.some((r) => r.boss)) void loadBossLooks();
    // So does the impact test's pad, for every match but Kumite
    if (mode.kind !== 'kumite') void loadImpactKit();
    this.seed = seedFor(mode);
    this.sim = new Sim(mulberry32(this.seed), first, simOptionsFor(mode));
    // A rule of the day can change the number of hearts: redraw the row
    this.shown.hearts = -1;
    this.boss = null;
    this.hitsTaken = 0;
    // A friend's score to beat sits under the score
    this.shown.target = '';
    this.hud.target.hidden = !mode.rival;
    document.body.classList.toggle('rival', !!mode.rival);
    this.round = 0;
    this.passed = false;
    this.inputs = [];
    this.acc = 0;
    this.hitStop = 0;
    this.slowmo = 0;
    this.coach = new Coach(coach);
    this.touches.clear();
    this.countered = false;
    this.landings = [];
    this.counted = 0;
    this.lastReal = performance.now();
    this.impact = null;
    this.impacts = [];
    this.renderer.pad = null;
    this.renderer.reset();
    this.renderer.stage = stageFor(mode);
    this.renderer.tapHint = hint ? 1 : 0;
    this.sfx.setStage(this.renderer.stage);
    // The HUD switches to light text on the night stage
    document.body.dataset.stage = this.renderer.stage;
    this.enterBanner();
  }

  setPaused(paused: boolean): void {
    if (paused === this.paused || (paused && !this.playing)) return;
    this.paused = paused;
    this.inputs = [];
    if (paused) this.sfx.music.stop();
    else if (this.phase !== 'impact') this.sfx.music.start(bpmFor(this.renderer.stage, this.round));
    this.lastReal = performance.now();
    this.onPauseChange(paused);
  }

  /** Test hook: the impact test's state, if it's on: its reading, how the
   *  punch met the pad and how far off the right distance the pad is now. */
  impactState(): { value: number | null; maai: Maai | null; offset: number } | null {
    const pad = this.renderer.pad;
    if (!this.impact || !pad) return null;
    return { value: this.impact.value, maai: this.impact.maai, offset: padOffset(pad.step) };
  }

  /** Leave the current match without a result (pause menu → dojo). */
  abort(): void {
    this.sfx.music.stop();
    document.body.classList.remove('impact');
    this.impact = null;
    this.renderer.pad = null;
    this.phase = 'idle';
    this.renderer.tapHint = 0;
    this.paused = false;
    this.inputs = [];
  }

  private enterBanner(): void {
    const total = this.rounds.length;
    const sub =
      this.mode.kind === 'kumite'
        ? t('fight.kumite')
        : t('fight.roundOf', { round: this.round + 1, total });
    this.renderer.banner(t('fight.hajime'), sub, BANNER_TIME);
    this.phase = 'banner';
    this.phaseT = 0;
    const bpm = bpmFor(this.renderer.stage, this.round);
    this.updateIntensity();
    this.sfx.music.start(bpm);
    this.sfx.music.setTempo(bpm);
  }

  private bindInput(): void {
    this.canvas.addEventListener('pointerdown', (e) => {
      if (this.phase === 'impact' && !this.paused) {
        e.preventDefault();
        this.strikePad();
        return;
      }
      if (this.phase !== 'fight' || this.paused) return;
      e.preventDefault();
      const side: Side = e.clientX < this.canvas.clientWidth / 2 ? -1 : 1;
      this.keyboard = false;
      this.inputs.push({ t: e.timeStamp, action: side });
      if (e.pointerType !== 'mouse') {
        this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY, swiped: false });
      }
    });
    // A swipe struck where it came down, already: the coach says to just tap
    // (in the fight: one that slides on after the round is over doesn't count)
    this.canvas.addEventListener('pointermove', (e) => {
      const touch = this.touches.get(e.pointerId);
      if (!touch || touch.swiped || this.phase !== 'fight' || this.paused) return;
      if (Math.hypot(e.clientX - touch.x, e.clientY - touch.y) < SWIPE_PX) return;
      touch.swiped = true;
      this.coach.touched(true);
      // The tap rings come back with the tip, fading again with the hits
      if (this.coach.tapTip) this.renderer.tapHint = 1;
    });
    const lifted = (e: PointerEvent): void => {
      const touch = this.touches.get(e.pointerId);
      if (!touch) return;
      this.touches.delete(e.pointerId);
      const learned = touch.swiped ? 0 : this.coach.touched(false);
      if (learned) this.onCoached(learned);
    };
    this.canvas.addEventListener('pointerup', lifted);
    // Taken by the system (an edge swipe back, a palm): neither a tap nor a swipe
    this.canvas.addEventListener('pointercancel', (e) => void this.touches.delete(e.pointerId));
    this.hud.kiaiBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.phase === 'fight') this.inputs.push({ t: e.timeStamp, action: 'kiai' });
    });
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Escape' || e.code === 'KeyP') {
        if (this.playing) this.setPaused(!this.paused);
        return;
      }
      if (this.phase === 'impact' && !this.paused && !e.repeat) {
        if (
          ['Space', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'KeyA', 'KeyD', 'KeyW'].includes(
            e.code,
          )
        ) {
          e.preventDefault();
          this.strikePad();
        }
        return;
      }
      if (e.repeat || this.phase !== 'fight' || this.paused) return;
      const action: Input['action'] | null =
        e.code === 'ArrowLeft' || e.code === 'KeyA'
          ? -1
          : e.code === 'ArrowRight' || e.code === 'KeyD'
            ? 1
            : e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW'
              ? 'kiai'
              : null;
      if (action === null) return;
      e.preventDefault();
      this.keyboard = true;
      this.inputs.push({ t: e.timeStamp, action });
    });
  }

  private frame(now: number): void {
    const realDt = this.paused ? 0 : Math.min(MAX_FRAME, Math.max(0, (now - this.lastReal) / 1000));
    if (!this.paused) this.coach.update(this.sim, realDt, this.phase === 'fight');
    if (this.paused) {
      // Frozen: keep drawing the scene under the pause card
    } else if (this.phase === 'fight') {
      if (this.bot) this.botInputs(now);
      this.inputs.sort((a, b) => a.t - b.t);
      for (const input of this.inputs) {
        if (input.t > now || this.phase !== 'fight') break;
        if (input.t > this.lastReal) {
          this.advance(Math.min(MAX_FRAME, (input.t - this.lastReal) / 1000));
          this.lastReal = input.t;
        }
        if (this.phase !== 'fight') break;
        if (input.action === 'kiai') this.sim.kiai();
        else this.sim.strike(input.action);
        this.handle(this.sim.drainEvents());
      }
      this.inputs = this.inputs.filter((i) => i.t > now);
      if (now > this.lastReal) this.advance(Math.min(MAX_FRAME, (now - this.lastReal) / 1000));
    } else if (this.phase === 'cleared' || this.phase === 'ending') {
      // Let the last opponents fly off; no spawns and no input
      this.advance(realDt);
      this.phaseT += realDt;
      // The next round or the impact test once the karateka is back in kamae
      // (the sim waits during either)
      if (
        this.phase === 'cleared' &&
        this.phaseT >= this.clearedFor &&
        this.sim.state.time >= this.renderer.settled
      )
        this.afterRound();
      else if (this.phase === 'ending' && this.phaseT >= ENDING_TIME) this.finish();
    } else if (this.phase === 'impact' && this.impact) {
      this.tickImpact(realDt);
    } else if (this.phase === 'banner') {
      this.phaseT += realDt;
      if (this.phaseT >= BANNER_TIME) {
        this.phase = 'fight';
        this.lastReal = now;
      }
    }
    this.lastReal = now;
    this.governQuality(realDt);
    const { now: cue, near, label } = this.coach;
    this.renderer.coach = { now: cue, near, label };
    this.renderer.draw(this.sim, realDt);
    if (this.playing) this.updateHud();
    requestAnimationFrame((t) => this.frame(t));
  }

  /** Advance sim time by `real` seconds of wall time, honoring hit-stop and slow-mo. */
  private advance(real: number): void {
    let remaining = real;
    while (remaining > 1e-6 && this.sim.state.running) {
      const scale = this.hitStop > 0 ? 0.04 : this.slowmo > 0 ? T.kiaiTimeScale : this.coach.pace;
      const limit = this.hitStop > 0 ? this.hitStop : this.slowmo > 0 ? this.slowmo : remaining;
      const chunk = Math.min(remaining, limit);
      remaining -= chunk;
      if (this.hitStop > 0) this.hitStop -= chunk;
      else if (this.slowmo > 0) this.slowmo -= chunk;
      this.acc += chunk * scale;
      while (this.acc >= T.dt) {
        this.acc -= T.dt;
        this.sim.step();
        this.handle(this.sim.drainEvents());
        this.playLandings();
      }
    }
  }

  private handle(events: SimEvent[]): void {
    if (events.length === 0) return;
    const s = this.sim.state;
    this.renderer.onEvents(events, s.time);
    const learned = this.coach.onEvents(events, s.time);
    if (learned) this.onCoached(learned);
    const struck = events.some((ev) => ev.type === 'hit');
    for (const ev of events) {
      switch (ev.type) {
        case 'hit': {
          const sound = {
            judgement: ev.judgement,
            id: ev.enemy.id,
            combo: this.sim.state.combo,
            kick: this.renderer.lastWasKick,
            head: this.renderer.lastWasHead,
            target: ev.enemy.kind,
            side: ev.enemy.side,
            killed: ev.killed,
          };
          this.sfx.swing(sound);
          if (ev.judgement !== 'good') this.countered = ev.judgement === 'counter';
          // The hit registers as the strike gets there: its impact and the
          // chain's note, the freeze-frame, the buzz, and on the HUD
          const chain = ev.chain;
          const final = ev.killed && s.remaining === 0;
          this.onContact(() => {
            this.sfx.landed(sound);
            if (chain >= 2) this.sfx.chain(chain, sound.side);
            if (sound.judgement !== 'good') this.hitStop = HIT_STOP;
            // Final blow of a round: let it hang in slow motion, a boss's longer
            if (final) this.slowmo = ev.enemy.boss ? BOSS.koSlowmo : FINAL_BLOW_SLOWMO;
            navigator.vibrate?.(ev.killed ? 12 : 6);
            this.updateIntensity();
          });
          const lands = s.time + this.renderer.lastContact;
          this.counted = Math.max(this.counted, lands);
          // A knocked-down opponent hits the floor after its flight
          if (ev.killed)
            this.landings.push({ at: lands + AIRBORNE, play: () => this.sfx.fall(sound.side) });
          break;
        }
        case 'blocked': {
          const side = ev.enemy.side;
          this.onContact(() => this.sfx.blocked(side));
          break;
        }
        case 'whiff':
          this.sfx.whiff(ev.side);
          break;
        case 'hurt':
          this.hitsTaken++;
          this.sfx.hurt(ev.enemy.side);
          this.updateIntensity();
          navigator.vibrate?.(60);
          break;
        case 'windup': {
          const e = ev.enemy;
          const heavy = e.kind === 'heavy' || e.kind === 'grappler';
          this.sfx.windup(e.side, heavy, KINDS[e.kind].windup / e.windup);
          break;
        }
        case 'spawn':
          // The stage boss walks in: his name over the mat, the music lifts
          if (ev.enemy.boss) {
            this.boss = ev.enemy.boss;
            this.renderer.banner(t(`boss.${ev.enemy.boss}.name`), t('fight.bossSub'), BANNER_TIME);
            this.sfx.bossIn(ev.enemy.side);
            this.updateIntensity();
          }
          break;
        case 'act': {
          // A boss's act ends as the strike that ended it lands
          const { id, side } = ev.enemy;
          const knocked = (): void => {
            this.sfx.bossAct(id, side);
            navigator.vibrate?.(30);
          };
          if (struck) this.onContact(knocked);
          else knocked();
          break;
        }
        case 'kiai':
          this.sfx.kiai();
          this.slowmo = T.kiaiSlowmo;
          navigator.vibrate?.([20, 30, 40]);
          break;
        case 'roundclear': {
          // Called as the last strike lands; a boss's knockout gets its slow
          // motion before the impact test
          const wait = Math.max(0, this.counted - s.time);
          const shows = this.boss ? BOSS.koSlowmo + 0.5 : CLEARED_TIME;
          this.clearedFor = wait + shows;
          const sub = this.boss
            ? t('fight.bossDown', { name: t(`boss.${this.boss}.name`) })
            : t('fight.roundCleared');
          this.landings.push({
            at: s.time + wait,
            play: () => {
              this.sfx.roundClear();
              this.renderer.banner(t('fight.yame'), sub, shows);
            },
          });
          this.boss = null;
          this.phase = 'cleared';
          this.phaseT = 0;
          this.inputs = [];
          break;
        }
        case 'gameover':
          this.sfx.gameover();
          this.passed = false;
          this.phase = 'ending';
          this.phaseT = 0;
          this.inputs = [];
          break;
        default:
          break;
      }
    }
  }

  private nextRound(): void {
    const next = this.rounds[this.round + 1];
    if (!next) {
      this.passed = true;
      this.finish();
      return;
    }
    this.round++;
    // A breather between rounds: one heart back
    this.sim.heal(1);
    this.sim.beginRound(next);
    this.enterBanner();
  }

  /** Play `sound` when the strike just shown reaches the opponent. */
  private onContact(sound: () => void): void {
    this.landings.push({ at: this.sim.state.time + this.renderer.lastContact, play: sound });
  }

  private playLandings(): void {
    const now = this.sim.state.time;
    const due = this.landings.filter((l) => l.at <= now + 1e-9);
    if (due.length === 0) return;
    this.landings = this.landings.filter((l) => l.at > now + 1e-9);
    for (const l of due) l.play();
  }

  // ---- Impact test: a full-power gyaku-zuki into a measuring pad ----

  /** The impact test closes an exam or daily exam, after its last round. */
  private afterRound(): void {
    const last = this.round + 1 >= this.rounds.length;
    if (last && this.mode.kind !== 'kumite') this.enterImpact();
    else this.nextRound();
  }

  /** Whose impact test it is: the exam's, or the blue belt's for the daily exam. */
  private impactExam(): number {
    return this.mode.kind === 'exam' ? this.mode.exam : 4;
  }

  private enterImpact(): void {
    this.sfx.music.stop();
    this.impact = { meter: 0, t: 0, value: null, swing: 0, lands: PAD_LANDS, maai: null };
    const exam = this.impactExam();
    this.renderer.pad = {
      meter: 0,
      value: null,
      t: 0,
      lands: PAD_LANDS,
      step: maaiAt(0, exam, this.seed),
      maai: null,
    };
    // Any tap strikes the pad: no tap rings on the halves
    this.renderer.tapHint = 0;
    const hint = exam >= MAAI_FROM ? 'fight.impactHintMaai' : 'fight.impactHint';
    this.renderer.banner(t('fight.impactTest'), t(hint), IMPACT_BANNER);
    // The fight's HUD steps aside: no Kiai to charge here
    document.body.classList.add('impact');
    this.sim.state.player.side = 1;
    // The sim is frozen during the test: drop bodies still flying off
    this.sim.state.enemies = [];
    this.phase = 'impact';
    this.phaseT = 0;
    this.inputs = [];
  }

  private tickImpact(dt: number): void {
    const im = this.impact!;
    const exam = this.impactExam();
    im.t += dt;
    if (im.value === null) {
      // While the call is read, the meter waits at the bottom
      im.swing = Math.max(0, im.t - IMPACT_INTRO);
      im.meter = meterAt(im.swing, exam, this.seed);
      if (this.bot && im.meter >= 0.9 && maaiOf(padMeet(im.swing, exam, this.seed).d) === 'in')
        this.strikePad();
      else if (im.swing > (exam >= MAAI_FROM ? IMPACT_AUTO_MAAI : IMPACT_AUTO)) this.strikePad();
    } else if (im.t > IMPACT_AFTER) {
      this.impact = null;
      this.renderer.pad = null;
      this.nextRound();
      return;
    }
    this.renderer.pad = {
      meter: im.meter,
      value: im.value,
      t: im.t,
      lands: im.lands,
      // Struck, the senpai stops where the punch meets the pad
      step: maaiAt(im.swing + (im.value === null ? 0 : Math.min(im.t, im.lands)), exam, this.seed),
      maai: im.maai,
    };
    // The punch reaches the pad: its sound, the buzz, the reading and the
    // score; one that falls short only gets its call
    if (im.value !== null && im.t >= im.lands && im.t - dt < im.lands) {
      this.sim.state.score += im.value;
      if (im.maai !== 'far') {
        this.sfx.impact(im.value / IMPACT_MAX);
        navigator.vibrate?.(im.value >= IMPACT_MAX ? [30, 40, 60] : 25);
      }
      this.renderer.impactLanded(im.value, IMPACT_MAX, im.maai ?? 'in');
    }
  }

  private strikePad(): void {
    const im = this.impact;
    // A tap before the meter swings doesn't count
    if (!im || im.value !== null || im.t < IMPACT_INTRO) return;
    // How far off the pad is as the punch gets there
    const { d, lands } = padMeet(im.swing, this.impactExam(), this.seed);
    im.maai = maaiOf(d);
    im.value = Math.round(impactFor(im.meter) * maaiFactor(d));
    im.lands = lands;
    im.t = 0;
    this.impacts.push(im.value);
    if (im.maai === 'far') this.sfx.swing({ kick: false, side: 1 });
  }

  private finish(): void {
    const s = this.sim.state;
    const total = this.rounds.length;
    this.sfx.music.stop();
    const impacts = [...this.impacts];
    this.impact = null;
    document.body.classList.remove('impact');
    this.renderer.pad = null;
    this.phase = 'idle';
    this.renderer.tapHint = 0;
    this.onEnd({
      mode: this.mode,
      seed: this.seed,
      passed: this.passed,
      rounds: this.passed ? total : this.round,
      totalRounds: total,
      score: s.score,
      kills: s.kills,
      hits: s.hits,
      perfects: s.perfects,
      bestCombo: s.bestCombo,
      bestChain: s.bestChain,
      hitsTaken: this.hitsTaken,
      seconds: s.time,
      level: s.level - (this.rounds[0]?.level ?? 0),
      ...(impacts.length ? { impacts } : {}),
    });
  }

  /** Music layers build through the rounds; a long combo adds the rolls. */
  private updateIntensity(): void {
    const s = this.sim.state;
    const climbed = s.level - (this.rounds[0]?.level ?? 0);
    const base = this.mode.kind === 'kumite' ? Math.min(2, Math.floor(climbed / 3)) : this.round;
    this.sfx.music.setIntensity(Math.min(3, base + (s.combo >= 15 || this.boss ? 1 : 0)));
  }

  /**
   * If frames keep running slow during a fight (slow phone), drop the render
   * resolution a step: timing matters more than crispness. Never goes back up
   * within a visit, so it can't oscillate.
   */
  private governQuality(dt: number): void {
    if (this.phase !== 'fight' || dt <= 0 || document.hidden) return;
    this.frameAvg += (dt - this.frameAvg) * 0.05;
    this.slowFor = this.frameAvg > SLOW_FRAME ? this.slowFor + dt : 0;
    const current = Math.min(this.renderer.maxDpr, window.devicePixelRatio || 1);
    if (this.slowFor > 2 && current > 1) {
      this.renderer.maxDpr = Math.max(1, current - 0.5);
      this.renderer.resize();
      this.slowFor = 0;
      track('Perf', 'LowerResolution', String(this.renderer.maxDpr));
    }
  }

  private botInputs(now: number): void {
    for (const side of [-1, 1] as Side[]) {
      const e = this.sim.front(side);
      if (!e || !inReach(e) || e.d > T.reach || e.state === 'dead') continue;
      if (e.kind === 'blocker' && e.state !== 'windup') continue;
      if (this.inputs.some((i) => i.action === side)) continue;
      this.inputs.push({ t: now, action: side });
    }
    if (this.sim.state.kiai >= 1) this.inputs.push({ t: now, action: 'kiai' });
  }

  private updateHud(): void {
    const s = this.sim.state;
    if (s.hearts !== this.shown.hearts) {
      const lost = this.shown.hearts > s.hearts;
      // The heart a breather between rounds gives back pops in
      const gained = this.shown.hearts >= 0 && s.hearts > this.shown.hearts;
      this.shown.hearts = s.hearts;
      this.hud.hearts.replaceChildren(
        ...Array.from({ length: this.sim.maxHearts }, (_, i) => {
          const heart = document.createElement('span');
          heart.textContent = '♥';
          heart.className =
            i < s.hearts
              ? gained && i === s.hearts - 1
                ? 'heart gaining'
                : 'heart'
              : lost && i === s.hearts
                ? 'heart gone losing'
                : 'heart gone';
          return heart;
        }),
      );
    }
    // A hit counts as its strike lands: till the last on its way does, the
    // score, PERFECT chain, round and Kiai wait
    if (s.running && s.time < this.counted) return;
    if (s.score !== this.shown.score) {
      const up = s.score > this.shown.score && this.shown.score >= 0;
      this.shown.score = s.score;
      this.hud.score.textContent = fmt(s.score);
      if (up) bump(this.hud.score);
    }
    // One streak on show, the PERFECT chain (the combo only scores); the
    // impact test is no fight: no chain, its name in place of the round
    const impact = this.phase === 'impact';
    const chain = impact ? 0 : s.chain;
    // Named as the call over the opponent names it: by the hit that made it
    const streak =
      chain >= 3 ? t(this.countered ? 'hud.counterChain' : 'hud.chain', { count: chain }) : '';
    if (streak !== this.shown.chain) {
      this.shown.chain = streak;
      this.hud.chain.textContent = streak;
      if (streak) bump(this.hud.chain);
    }
    const total = this.rounds.length;
    const round = impact
      ? t('fight.impactTest')
      : s.remaining === null
        ? t('hud.level', { level: s.level - (this.rounds[0]?.level ?? 0) + 1 })
        : this.boss
          ? t('hud.boss', { round: this.round + 1, total, name: t(`boss.${this.boss}.name`) })
          : t('hud.round', { round: this.round + 1, total, left: Math.max(0, s.remaining) });
    if (round !== this.shown.round) {
      this.shown.round = round;
      this.hud.round.textContent = round;
    }
    const rival = this.mode.rival;
    if (rival) {
      const target =
        s.score < rival.score
          ? t('hud.target', { score: rival.score })
          : t('hud.ahead', { score: s.score - rival.score });
      if (target !== this.shown.target) {
        this.shown.target = target;
        this.hud.target.textContent = target;
        this.hud.target.classList.toggle('ahead', s.score >= rival.score);
      }
    }
    const tier = chainTier(s.chain);
    if (tier !== this.shown.tier) {
      // A PERFECT chain charges the Kiai faster: the bar glows while it lasts
      this.shown.tier = tier;
      this.hud.kiaiBtn.classList.toggle('chain', tier > 0);
    }
    if (s.kiai !== this.shown.kiai) {
      this.shown.kiai = s.kiai;
      this.hud.kiaiFill.style.transform = `scaleX(${s.kiai})`;
      this.hud.kiaiBtn.classList.toggle('ready', s.kiai >= 1);
    }
    // A swipe: the coach says to just tap (in the Kiai tip's place, for its few seconds)
    const tapTip = this.coach.tapTip;
    if (tapTip !== this.shown.tapTip) {
      this.shown.tapTip = tapTip;
      this.hud.tapTip.textContent = tapTip ? t('coach.tap') : '';
      this.hud.tapTip.hidden = !tapTip;
    }
    // The first full bar: the coach says what it's for
    const tip =
      this.coach.kiaiReady && !tapTip ? t(this.keyboard ? 'coach.kiaiKey' : 'coach.kiaiTap') : '';
    if (tip !== this.shown.tip) {
      this.shown.tip = tip;
      this.hud.kiaiTip.textContent = tip;
      this.hud.kiaiTip.hidden = !tip;
    }
  }
}
